import { useMemo } from 'react'
import { Card, CardContent } from '@/components/ui'
import { formatCurrency } from '@/lib/utils'
import { Users, UserCheck, TrendingUp, TrendingDown, Minus, BarChart3, Building2, MapPin, Bot, FileText } from 'lucide-react'
import { computeFunnel, computeAcquiredStats, computePriceStats, computeZoneStats, computeTypeStats, computeMonthlyStats } from '@/lib/export/pdf-stats'
import type { LeadWithValuation } from '@/types'

interface Props {
  leads: LeadWithValuation[]
}

const FUNNEL_COLORS = ['bg-green-500', 'bg-blue-500', 'bg-orange-500', 'bg-emerald-500']

export function StatisticheDashboard({ leads }: Props) {
  const funnel = useMemo(() => {
    const data = computeFunnel(leads)
    return data.map((item, i) => ({ ...item, color: FUNNEL_COLORS[i] }))
  }, [leads])

  const acquiredStats = useMemo(() => computeAcquiredStats(leads), [leads])
  const priceStats = useMemo(() => computePriceStats(leads), [leads])
  const zoneStats = useMemo(() => computeZoneStats(leads), [leads])
  const typeStats = useMemo(() => computeTypeStats(leads), [leads])
  const monthlyStats = useMemo(() => computeMonthlyStats(leads), [leads])

  const sourceStats = useMemo(() => {
    const fromChat = leads.filter(l => l.source === 'ai_chat').length
    const fromForm = leads.length - fromChat
    const chatPct = leads.length > 0 ? Math.round((fromChat / leads.length) * 100) : 0
    const formPct = leads.length > 0 ? 100 - chatPct : 0
    return { fromChat, fromForm, chatPct, formPct }
  }, [leads])

  if (leads.length === 0) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="border-0 shadow-md">
          <CardContent className="p-16 text-center">
            <div className="bg-gray-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <BarChart3 className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Nessun dato disponibile</h3>
            <p className="text-gray-500">Le statistiche appariranno quando ci saranno lead.</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
      {/* Row 1: KPI Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 admin-stagger">
        {/* Funnel Lead */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Users className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Funnel Lead</h3>
            </div>
            <div className="space-y-3">
              {funnel.map(item => (
                <div key={item.label}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-gray-600">{item.label}</span>
                    <span className="font-medium text-gray-900">{item.count} ({Math.round(item.pct)}%)</span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={`h-2 ${item.color} rounded-full transition-all duration-700 ease-out`}
                      style={{ width: `${Math.max(item.pct, 2)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Contatti Acquisiti */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <UserCheck className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Contatti Acquisiti</h3>
            </div>
            <div className="text-center">
              <p className="text-5xl font-bold text-emerald-600 animate-count-up">{acquiredStats.count}</p>
              <p className="text-sm text-gray-500 mt-2">
                {acquiredStats.rate}% dei {leads.length} lead totali
              </p>
              <div className="mt-3 h-2 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-2 bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                  style={{ width: `${acquiredStats.rate}%` }}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Prezzo Medio/mq */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Prezzo Medio/mq</h3>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-primary-600 animate-count-up">{formatCurrency(priceStats.avg)}</p>
              <div className="flex justify-center gap-4 mt-3 text-sm">
                <span className="text-gray-500">Min: <span className="font-medium text-gray-700">{formatCurrency(priceStats.min)}</span></span>
                <span className="text-gray-500">Max: <span className="font-medium text-gray-700">{formatCurrency(priceStats.max)}</span></span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Fonte Lead */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bot className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Fonte Lead</h3>
            </div>
            <div className="space-y-3">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <FileText className="h-3.5 w-3.5" />
                    Form
                  </span>
                  <span className="font-medium text-gray-900">{sourceStats.fromForm} ({sourceStats.formPct}%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-blue-500 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${Math.max(sourceStats.formPct, 2)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="flex items-center gap-1.5 text-gray-600">
                    <Bot className="h-3.5 w-3.5" />
                    Chat AI
                  </span>
                  <span className="font-medium text-gray-900">{sourceStats.fromChat} ({sourceStats.chatPct}%)</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div
                    className="h-2 bg-purple-500 rounded-full transition-all duration-700 ease-out"
                    style={{ width: `${Math.max(sourceStats.chatPct, 2)}%` }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 admin-stagger">
        {/* Zone più richieste */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <MapPin className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Zone più richieste</h3>
            </div>
            {zoneStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-3 font-medium text-gray-500">Zona</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Lead</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Val. Medio</th>
                      <th className="text-right py-2 pl-3 font-medium text-gray-500">/mq</th>
                    </tr>
                  </thead>
                  <tbody>
                    {zoneStats.map(z => (
                      <tr key={z.zone} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="py-2 pr-3 font-medium text-gray-900">{z.zone}</td>
                        <td className="text-right py-2 px-3 text-gray-700">{z.count}</td>
                        <td className="text-right py-2 px-3 text-gray-700">{formatCurrency(z.avgValue)}</td>
                        <td className="text-right py-2 pl-3 text-gray-700">{formatCurrency(z.avgPricePerSqm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nessun dato disponibile.</p>
            )}
          </CardContent>
        </Card>

        {/* Distribuzione Tipologia */}
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Building2 className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Distribuzione Tipologia</h3>
            </div>
            {typeStats.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 pr-3 font-medium text-gray-500">Tipo</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Lead</th>
                      <th className="text-right py-2 px-3 font-medium text-gray-500">Sup. Media</th>
                      <th className="text-right py-2 pl-3 font-medium text-gray-500">Val. Medio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {typeStats.map(t => (
                      <tr key={t.type} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                        <td className="py-2 pr-3 font-medium text-gray-900 capitalize">{t.type}</td>
                        <td className="text-right py-2 px-3 text-gray-700">{t.count}</td>
                        <td className="text-right py-2 px-3 text-gray-700">{t.avgArea} mq</td>
                        <td className="text-right py-2 pl-3 text-gray-700">{formatCurrency(t.avgValue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">Nessun dato disponibile.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2.5: Confronto Zone — Domanda vs Prezzo */}
      {zoneStats.length > 1 && (
        <Card className="border-0 shadow-md admin-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="h-5 w-5 text-gray-500" />
              <h3 className="font-semibold text-gray-900">Confronto Zone: Domanda vs Prezzo/mq</h3>
            </div>
            <div className="space-y-3">
              {zoneStats.map(z => {
                const maxCount = Math.max(...zoneStats.map(s => s.count))
                const maxPrice = Math.max(...zoneStats.map(s => s.avgPricePerSqm))
                const countPct = maxCount > 0 ? (z.count / maxCount) * 100 : 0
                const pricePct = maxPrice > 0 ? (z.avgPricePerSqm / maxPrice) * 100 : 0
                return (
                  <div key={z.zone}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="font-medium text-gray-900 truncate max-w-[180px]">{z.zone}</span>
                      <span className="text-xs text-gray-500">{z.count} lead · {formatCurrency(z.avgPricePerSqm)}/mq</span>
                    </div>
                    <div className="flex gap-1">
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden" title={`Domanda: ${z.count} lead`}>
                        <div
                          className="h-3 bg-blue-500 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${Math.max(countPct, 3)}%` }}
                        />
                      </div>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden" title={`Prezzo: ${formatCurrency(z.avgPricePerSqm)}/mq`}>
                        <div
                          className="h-3 bg-emerald-500 rounded-full transition-all duration-700 ease-out"
                          style={{ width: `${Math.max(pricePct, 3)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                )
              })}
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-2 bg-blue-500 rounded-sm" />
                  Domanda (n. lead)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-3 h-2 bg-emerald-500 rounded-sm" />
                  Prezzo medio/mq
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Row 3: Lead nel tempo */}
      <Card className="border-0 shadow-md admin-card animate-slide-in-up">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-5 w-5 text-gray-500" />
            <h3 className="font-semibold text-gray-900">Lead nel tempo</h3>
          </div>
          {monthlyStats.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-2 pr-3 font-medium text-gray-500">Mese</th>
                    <th className="text-right py-2 px-3 font-medium text-gray-500">Lead</th>
                    <th className="text-right py-2 pl-3 font-medium text-gray-500">Trend</th>
                  </tr>
                </thead>
                <tbody>
                  {monthlyStats.map(m => (
                    <tr key={m.key} className="border-b border-gray-100 last:border-0 hover:bg-gray-50 transition-colors">
                      <td className="py-2 pr-3 font-medium text-gray-900">{m.label}</td>
                      <td className="text-right py-2 px-3 text-gray-700">{m.count}</td>
                      <td className="text-right py-2 pl-3">
                        {m.trend === 'up' && <TrendingUp className="h-4 w-4 text-green-500 inline" />}
                        {m.trend === 'down' && <TrendingDown className="h-4 w-4 text-red-500 inline" />}
                        {m.trend === 'same' && <Minus className="h-4 w-4 text-gray-400 inline" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500">Nessun dato disponibile.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
